import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Users, UserPlus, Trash2, Mail, Crown, Send, BarChart3, Flame } from "lucide-react";

import { InviteMemberDialog } from "@/components/team/InviteMemberDialog";
import { SendReportDialog } from "@/components/team/SendReportDialog";
import { ChallengeDialog } from "@/components/team/ChallengeDialog";
import { ProvisionUserDialog } from "@/components/team/ProvisionUserDialog";

export default function Team() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [globalChallenges, setGlobalChallenges] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Commercial");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [config, setConfig] = useState({ manager_email: "", report_day: "Mon", report_hour: 8, sharing_rule: "assigned", challenges_enabled: false });
  const [reportDialog, setReportDialog] = useState(false);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [challengeOpen, setChallengeOpen] = useState(false);
  const [challengeForm, setChallengeForm] = useState({ title: "", description: "", channel: "Road Maps", xp: 50 });
  const [creatingChallenge, setCreatingChallenge] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [provisionForm, setProvisionForm] = useState({ email: "", name: "", role: "Commercial" });
  const [provisionResult, setProvisionResult] = useState(null);
  const [provisioning, setProvisioning] = useState(false);

  const load = async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/team"),
        api.get("/settings").catch(() => ({ data: {} })),
      ]);
      setData(t.data);
      setGlobalChallenges(!!s.data?.challenges_enabled);
      if (t.data.team) {
        setConfig({
          manager_email: t.data.team.manager_email || "",
          report_day: t.data.team.report_day || "Mon",
          report_hour: t.data.team.report_hour || 8,
          sharing_rule: t.data.team.sharing_rule || "assigned",
          challenges_enabled: !!t.data.team.challenges_enabled,
        });
      }
    } catch { toast.error("Erreur chargement équipe"); }
  };
  useEffect(() => { load(); }, []);

  const createChallenge = async () => {
    if (!challengeForm.title) return;
    setCreatingChallenge(true);
    try {
      const { data } = await api.post("/team/challenges", challengeForm);
      toast.success(`Défi envoyé à ${data.created} membre${data.created > 1 ? "s" : ""}`);
      setChallengeForm({ title: "", description: "", channel: "Road Maps", xp: 50 });
      setChallengeOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seul le manager peut créer des défis");
    } finally {
      setCreatingChallenge(false);
    }
  };

  const invite = async () => {
    if (!inviteEmail) return;
    try {
      const { data } = await api.post("/team/invite", { email: inviteEmail, role: inviteRole });
      toast.success(data.email_sent ? "Invitation envoyée" : "Invitation enregistrée (email en mode mock)");
      setInviteEmail(""); setInviteRole("Commercial"); setInviteOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur invitation");
    }
  };

  const remove = async (uid) => {
    if (!confirm("Retirer ce membre ?")) return;
    await api.delete(`/team/members/${uid}`);
    toast.success("Membre retiré");
    load();
  };

  const saveConfig = async () => {
    try {
      await api.put("/team/config", config);
      toast.success("Paramètres enregistrés");
    } catch { toast.error("Erreur"); }
  };

  const sendReport = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/team/report/send", { manager_email: config.manager_email, comment });
      if (data.sent) toast.success(`Rapport envoyé à ${data.recipient}`);
      else toast.success(`Rapport préparé pour ${data.recipient} (mode mock — configure RESEND_API_KEY pour envoyer réellement)`);
      setReportDialog(false); setComment("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur envoi"); }
    finally { setSending(false); }
  };

  const provisionUser = async () => {
    if (!provisionForm.email || !provisionForm.name) {
      toast.error("Nom et email requis");
      return;
    }
    setProvisioning(true);
    try {
      const { data } = await api.post("/team/provision-user", provisionForm);
      setProvisionResult(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création");
    } finally { setProvisioning(false); }
  };

  const resetMemberPassword = async (uid) => {
    if (!window.confirm("Réinitialiser le mot de passe de ce commercial ?")) return;
    try {
      const { data } = await api.post("/team/reset-password", { user_id: uid });
      window.prompt("Nouveau mot de passe temporaire (copiez-le) :", data.temp_password);
      toast.success("Mot de passe réinitialisé");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const plan = user?.plan || "";
  const isEntreprise = plan.toLowerCase().includes("entreprise") || plan.toLowerCase().includes("white");

  if (!data) return <AppShell><div className="grid place-items-center py-24"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div></AppShell>;

  return (
    <AppShell>
      <div className="px-5 pt-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="rounded-full -ml-2" data-testid="back"><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>
      </div>
      <div className="px-5 mt-2">
        <h1 className="font-display text-3xl font-bold text-[var(--text)] flex items-center gap-2" data-testid="team-title"><Users className="w-6 h-6 text-emerald-400" />Équipe</h1>
      </div>

      {!isEntreprise && (
        <div className="mx-5 mt-4 bg-amber-500/10 border border-amber-200 rounded-2xl p-4" data-testid="upgrade-banner">
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong>Plan Entreprise requis.</strong> Vous pouvez explorer la page, mais l'invitation de membres nécessite un plan <strong>Entreprise</strong> ou <strong>White Label</strong>.
              <Link to="/pricing" className="text-emerald-400 font-semibold ml-1">Mettre à niveau →</Link>
            </div>
          </div>
        </div>
      )}

      <div className="px-5 mt-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display font-bold text-[var(--text)]">Membres ({data.members.length})</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setInviteOpen(true)} disabled={!isEntreprise} className="rounded-full" data-testid="open-invite"><Mail className="w-4 h-4 mr-1" />Inviter</Button>
            <Button size="sm" onClick={() => { setProvisionResult(null); setProvisionForm({ email: "", name: "", role: "Commercial" }); setProvisionOpen(true); }} disabled={!isEntreprise} className="rounded-full bg-emerald-500/100 hover:bg-emerald-500" data-testid="open-provision">
              <UserPlus className="w-4 h-4 mr-1" />Créer compte
            </Button>
          </div>
        </div>
        <div className="bg-[var(--surface)] rounded-xl border border-[var(--border-soft)] shadow-sm divide-y">
          {data.members.map((m) => (
            <div key={m.user_id} className="p-3 flex items-center gap-3" data-testid={`member-${m.user_id}`}>
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 grid place-items-center text-white font-bold">{(m.name || m.email).charAt(0).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[var(--text)] truncate flex items-center gap-2">
                  {m.name || m.email}
                  {m.provisioned && <span className="text-[9px] bg-emerald-500/15 text-emerald-300 rounded-full px-1.5 py-0.5 font-bold">Provisionné</span>}
                </div>
                <div className="text-xs text-zinc-500 truncate">{m.email}</div>
              </div>
              <span className={`text-[10px] rounded-full px-2 py-0.5 font-bold uppercase tracking-wider ${m.role === "Admin" ? "bg-emerald-500/15 text-emerald-300" : "bg-[var(--surface-alt)] text-zinc-400"}`}>{m.role}</span>
              {m.user_id !== user?.user_id && (
                <>
                  {m.provisioned && (
                    <button onClick={() => resetMemberPassword(m.user_id)} title="Réinitialiser mot de passe" className="p-1.5 text-emerald-400 hover:bg-emerald-500/10 rounded-lg" data-testid={`reset-pwd-${m.user_id}`}>
                      <Crown className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => remove(m.user_id)} className="p-1.5 text-red-500 hover:bg-rose-500/100/10 rounded-lg" data-testid={`remove-${m.user_id}`}><Trash2 className="w-4 h-4" /></button>
                </>
              )}
            </div>
          ))}
        </div>

        {data.pending.length > 0 && (
          <>
            <h2 className="font-display font-bold text-[var(--text)] mt-5 mb-2">Invitations en attente</h2>
            <div className="bg-[var(--surface)] rounded-xl border border-[var(--border-soft)] shadow-sm divide-y">
              {data.pending.map((p) => (
                <div key={p.invitation_id} className="p-3 flex items-center gap-3" data-testid={`pending-${p.invitation_id}`}>
                  <Mail className="w-5 h-5 text-zinc-500" />
                  <div className="flex-1 text-sm">
                    <div className="font-semibold text-[var(--text)]">{p.email}</div>
                    <div className="text-xs text-zinc-500">Invité comme {p.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {globalChallenges && (
        <div className="px-5 mt-6">
          <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-[var(--text)] flex items-center gap-2">
                  <Flame className="w-4 h-4 text-orange-500" />
                  Mode Défis
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {config.challenges_enabled ? "Actif pour votre équipe" : "Désactivé"}
                </div>
              </div>
              <Switch
                checked={!!config.challenges_enabled}
                onCheckedChange={async (v) => {
                  setConfig({ ...config, challenges_enabled: v });
                  try {
                    await api.put("/team/config", { ...config, challenges_enabled: v });
                    toast.success(v ? "Défis activés pour l'équipe" : "Défis désactivés");
                  } catch { toast.error("Erreur"); }
                }}
                data-testid="toggle-team-challenges"
              />
            </div>
            {config.challenges_enabled && (
              <div className="mt-4 flex items-center justify-between gap-3">
                <div className="text-sm text-zinc-300">Proposer un défi à l'équipe</div>
                <Button size="sm" onClick={() => setChallengeOpen(true)} className="rounded-full bg-gradient-to-br from-emerald-500 to-orange-500 h-9 text-xs" data-testid="open-challenge">
                  <Flame className="w-3.5 h-3.5 mr-1" />
                  Créer
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="px-5 mt-6">
        <h2 className="font-display font-bold text-[var(--text)] mb-2">Rapport N+1</h2>
        <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)] shadow-sm space-y-3">
          <div>
            <Label>Email du manager N+1</Label>
            <Input value={config.manager_email} onChange={(e) => setConfig({ ...config, manager_email: e.target.value })} type="email" placeholder="manager@entreprise.fr" className="rounded-xl mt-1" data-testid="manager-email" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Jour d'envoi</Label>
              <Select value={config.report_day} onValueChange={(v) => setConfig({ ...config, report_day: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="report-day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[["Mon", "Lundi"], ["Tue", "Mardi"], ["Wed", "Mercredi"], ["Thu", "Jeudi"], ["Fri", "Vendredi"]].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Heure</Label>
              <Select value={String(config.report_hour)} onValueChange={(v) => setConfig({ ...config, report_hour: Number(v) })}>
                <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[7, 8, 9, 10, 11, 12, 14, 16, 18].map((h) => <SelectItem key={h} value={String(h)}>{h}h</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Règle de partage du tunnel</Label>
            <Select value={config.sharing_rule} onValueChange={(v) => setConfig({ ...config, sharing_rule: v })}>
              <SelectTrigger className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous voient tout</SelectItem>
                <SelectItem value="assigned">Chacun voit ses leads assignés</SelectItem>
                <SelectItem value="region">Partage par région</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button onClick={saveConfig} variant="outline" className="rounded-full flex-1" data-testid="save-config">Enregistrer</Button>
            <Button onClick={() => setReportDialog(true)} disabled={!config.manager_email} className="rounded-full flex-1 bg-emerald-500/100 hover:bg-emerald-500" data-testid="send-report"><Send className="w-4 h-4 mr-1" />Envoyer maintenant</Button>
          </div>
        </div>
      </div>

      <div className="px-5 mt-5">
        <Link to="/team/dashboard">
          <Button className="w-full rounded-full bg-slate-900 hover:bg-slate-800 h-12 font-semibold" data-testid="manager-dashboard-link"><BarChart3 className="w-4 h-4 mr-2" />Ouvrir le dashboard manager</Button>
        </Link>
      </div>

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        email={inviteEmail}
        role={inviteRole}
        onEmailChange={setInviteEmail}
        onRoleChange={setInviteRole}
        onSubmit={invite}
      />

      <SendReportDialog
        open={reportDialog}
        onOpenChange={setReportDialog}
        managerEmail={config.manager_email}
        comment={comment}
        onCommentChange={setComment}
        onSubmit={sendReport}
        sending={sending}
      />

      <ChallengeDialog
        open={challengeOpen}
        onOpenChange={setChallengeOpen}
        form={challengeForm}
        onFormChange={setChallengeForm}
        onSubmit={createChallenge}
        submitting={creatingChallenge}
        memberCount={data?.members?.length || 0}
      />

      <ProvisionUserDialog
        open={provisionOpen}
        onOpenChange={(o) => { setProvisionOpen(o); if (!o) setProvisionResult(null); }}
        form={provisionForm}
        onFormChange={setProvisionForm}
        result={provisionResult}
        onSubmit={provisionUser}
        submitting={provisioning}
      />
    </AppShell>
  );
}
